import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import type { ZodTypeAny } from 'zod'
import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { TextField } from '@app/components/core/form/fields/TextField'
import { Toggle } from '@app/components/core/toggle/Toggle'
import { Alert } from '@app/components/core/alert/Alert'
import {
  ImageEditRow,
  type ExistingImage,
  type ImagePreview,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import { Text } from '@app/components/core/typography/Text'
import { CreationGuide } from '@app/pages/dashboard/initiative/create/blocks/CreationGuide'
import { useTranslations } from '@i18n/useLanguage'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { useDebounce } from '@app/util/hooks/useDebounce'
import { Skeleton } from '@app/components/core/skeleton/Skeleton'
import { type LocationCoverFormValues } from '../shared/eventValidation'
import { eventCreationSteps, type EventCreationStep } from '../shared/workflow'
import { StepValuesSync } from '../shared/StepValuesSync'
import { Image } from '@app/components/core/image/Image'
import LocationPinIllustration from '@app/assets/images/illustrations/location-pin.png'
import type { LocationType } from '../shared/eventTypes'

const CURRENT_STEP: EventCreationStep = eventCreationSteps.locationCover

// Leaflet/MapLibre touch `window` at import time, so the map must stay out of
// the SSR module graph — load it lazily in a client-only chunk.
const SingleMarkerMap = lazy(() =>
  import('@app/components/core/map/SingleMarkerMap').then((module) => ({
    default: module.SingleMarkerMap,
  })),
)

type Coordinates = { lat: number; long: number }

/**
 * Watches the `address` field, geocodes it (debounced) and renders a map
 * preview once coordinates are resolved. Must be rendered inside the Form so
 * it can read the field via react-hook-form context.
 */
const LocationMapPreview = () => {
  const client = useClient()
  const t = useTranslations().app.common
  const { setValue } = useFormContext<LocationCoverFormValues>()
  const address = useWatch<LocationCoverFormValues, 'address'>({
    name: 'address',
  })
  const trimmedAddress = (address ?? '').trim()
  const debouncedAddress = useDebounce(trimmedAddress, 600)
  const [coords, setCoords] = useState<Coordinates | null>(null)
  const [geocoding, setGeocoding] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!debouncedAddress) {
      setCoords(null)
      setGeocoding(false)
      setError(false)
      setValue('geo', undefined)
      return
    }

    let cancelled = false
    setGeocoding(true)
    setError(false)
    client
      .call('geocode-address', { street: debouncedAddress })
      .then((result) => {
        if (cancelled) {
          return
        }
        const geo = result as Coordinates | null
        if (
          geo &&
          typeof geo.lat === 'number' &&
          typeof geo.long === 'number'
        ) {
          setError(false)
          setCoords((prev) =>
            prev && prev.lat === geo.lat && prev.long === geo.long
              ? prev
              : { lat: geo.lat, long: geo.long },
          )
          setValue('geo', { lat: geo.lat, long: geo.long })
        } else {
          setError(true)
          setCoords(null)
          setValue('geo', undefined)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setCoords(null)
          setValue('geo', undefined)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGeocoding(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [client, debouncedAddress, setValue])

  // Loading while the debounce is still counting down or the geocode is in flight.
  const isLoading =
    trimmedAddress !== '' && (trimmedAddress !== debouncedAddress || geocoding)

  const map = useMemo(
    () =>
      coords ? (
        <SingleMarkerMap
          lat={coords.lat}
          long={coords.long}
          marker="pin"
          height="16rem"
        />
      ) : null,
    [coords],
  )

  if (isLoading) {
    return <Skeleton height="16rem" borderRadius="20px" />
  }

  if (error) {
    return (
      <Skeleton
        height="16rem"
        borderRadius="20px"
        error
        errorMessage={t.locationNotFoundMessage}
      />
    )
  }

  if (!map) {
    return null
  }

  return <Suspense fallback={null}>{map}</Suspense>
}

type Props = {
  schema: ZodTypeAny
  locationType: LocationType
  setLocationType: (t: LocationType) => void
  defaultValues: LocationCoverFormValues
  eventId: number | null
  existingImages: ExistingImage[]
  onImagesChanged: () => void
  newImages: ImagePreview[]
  setNewImages: React.Dispatch<React.SetStateAction<ImagePreview[]>>
  removedImages: number[]
  setRemovedImages: React.Dispatch<React.SetStateAction<number[]>>
  onValuesChange: (values: LocationCoverFormValues) => void
}

export const LocationCoverStep = ({
  schema,
  locationType,
  setLocationType,
  defaultValues,
  eventId,
  existingImages,
  onImagesChanged,
  newImages,
  setNewImages,
  removedImages,
  setRemovedImages,
  onValuesChange,
}: Props) => {
  const t = useTranslations().app.dashboard.event.creation
  const client = useClient()
  const { showFailToast } = useToast()

  useEffect(() => {
    if (!eventId) {
      return
    }

    if (removedImages.length > 0) {
      Promise.all(
        removedImages.map((fileId) => client.call('delete-file', { fileId })),
      )
        .then(() => {
          setRemovedImages([])
          onImagesChanged()
        })
        .catch(() => showFailToast())
    }

    if (newImages.length > 0) {
      Promise.all(
        newImages.map((preview) =>
          client.stream('upload-file', {
            mimeType: preview.file.type,
            contents: preview.file,
            size: preview.file.size,
            fileName: `event-cover-${Date.now()}-${preview.file.name}`,
            payload: { eventImage: { id: eventId } },
          }),
        ),
      )
        .then(() => {
          newImages.forEach((preview) => URL.revokeObjectURL(preview.preview))
          setNewImages([])
          onImagesChanged()
        })
        .catch(() => showFailToast())
    }
  }, [
    client,
    eventId,
    newImages,
    removedImages,
    setNewImages,
    setRemovedImages,
    onImagesChanged,
    showFailToast,
  ])

  const stepTitles = { [CURRENT_STEP]: t.steps.locationCover.title }
  const stepDescriptions = {
    [CURRENT_STEP]: t.steps.locationCover.description,
  }

  const locationTypeOptions = [
    { label: t.offlineEvent, value: 'offline' as LocationType },
    { label: t.onlineEvent, value: 'online' as LocationType },
  ]

  return (
    <CreationGuide
      steps={[CURRENT_STEP]}
      currentStep={CURRENT_STEP}
      stepTitles={stepTitles}
      stepDescriptions={stepDescriptions}
      illustration={
        <Image
          src={LocationPinIllustration}
          alt="Location Pin Illustration"
          style={{ maxWidth: '15rem' }}
        />
      }
    >
      <Form<LocationCoverFormValues>
        key={locationType}
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <Column gap="2rem" alignItems="stretch" style={{ marginTop: '1.5rem' }}>
          <Toggle
            options={locationTypeOptions}
            selectedOption={locationType}
            setSelectedOption={setLocationType}
            style={{ alignSelf: 'stretch' }}
          />
          <StepValuesSync<LocationCoverFormValues>
            onValuesChange={onValuesChange}
          />
          {locationType === 'offline' ? (
            <>
              <TextField<LocationCoverFormValues>
                name="address"
                label={t.locationLabel}
                placeholder={t.locationPlaceholder}
              />
              <LocationMapPreview />
            </>
          ) : (
            <TextField<LocationCoverFormValues>
              name="link"
              label={t.linkLabel}
              placeholder={t.linkPlaceholder}
            />
          )}
          <Column gap="0.5rem" alignItems="stretch">
            <Text.Medium style={{ fontWeight: 500 }}>
              {t.coverImageLabel}
            </Text.Medium>
            <ImageEditRow
              existingImages={existingImages}
              newImages={newImages}
              removedImages={removedImages}
              setNewImages={setNewImages}
              setRemovedImages={setRemovedImages}
              maxImages={1}
            />
            <Alert variant="info" size="medium" message={t.coverImageInfo} />
          </Column>
        </Column>
      </Form>
    </CreationGuide>
  )
}
